import React from "react";
import { Section } from "components/QnaComponents";
import { Row, Col, InputNumber, Input, Select, Button } from "antd";
import { useDispatch, useSelector } from "react-redux";
import {
	keyMemberRemoved,
	keyMembersReceived,
	keyMemberUpserted,
	selectAllKeyMembers,
	selectKeyMemberById,
} from "./keyMembersSlice";
import { getFormattedNumber, newId } from "utils/common";
import { selectFormStatus, selectQnaIsLocked } from "reducers/formSlice";
import { fetchApprovers } from "reducers/workflowsSlice";
import PersonSearchAd from "components/PersonSearchAd";
import PersonSearch from "components/PersonSearch";
import _ from "lodash";
import { selectGlobal } from "reducers/global";

const style = {
	padding: 10,
	borderBottom: "1px solid #ddd",
};

const DEFAULT_KEYMEMBERS = [
	{
		id: newId(),
		userId: "53AC6DF4-DE29-4DF5-AEE4-C53AD24F96EC",
		email: "", //adding email here as a unique identifier since we search for people in the AD by email without other Ids available
		fullName: "Cheng, Leslie",
		role: "Project Accountant",
		limit: null,
		limitTo: null,
		mandatory: true,
		disabled: true,
		noLimit: true,
	},
	{
		id: newId(),
		userId: null,
		email: "",
		fullName: null,
		role: "Project Manager (Admin)",
		limit: null,
		limitTo: null,
		mandatory: true,
		isBp: true,
		noLimit: true,
	},
	{
		id: newId(),
		userId: null,
		email: "",
		fullName: null,
		role: "Budget Holder",
		limit: null,
		limitTo: null,
		mandatory: true,
	},
];

const BUSINESS_PARTNER_ROLE_ID = "4E9353D7-0078-4535-8EDB-99333E8E438F";

const KeyMembers = () => {
	const dispatch = useDispatch();
	const data = useSelector(selectAllKeyMembers);
	const formStatus = useSelector(selectFormStatus);
	const isLocked = useSelector(selectQnaIsLocked);
	const [bps, setBps] = React.useState([]);

	React.useEffect(() => {
		let isMounted = true; //mutable flag
		const fetchData = async () => {
			const response = await dispatch(
				fetchApprovers({
					roleId: BUSINESS_PARTNER_ROLE_ID,
					organisationId: null,
					domain: "Test",
				})
			).unwrap();
			if (response && response.length && isMounted) {
				const trimmed = response.map((d) => {
					return {
						fullName: d.lastName + ", " + d.firstName,
						userId: d.userId,
						jobTitle: d.jobTitle,
					};
				});
				const data = _.uniqWith(trimmed, _.isEqual);
				const sortedData = _.sortBy(data, (a) => a.fullName);
				setBps(sortedData); //check mounted flag
			}
		};
		fetchData();
		return () => {
			isMounted = false;
		}; //clean up, mounted untrue
	}, [dispatch]);

	//load default key members, if none exist
	React.useEffect(() => {
		if (!data || data.length === 0) {
			dispatch(keyMembersReceived(DEFAULT_KEYMEMBERS));
		}
	}, [dispatch, data]);

	return (
		<Section size="large">
			<Row justify="space-between">
				<Col>
					<h3>Key Members and Project Expenditure Approval Limit</h3>
					<p>
						If no Project Expenditure Approval Limits are assigned,
						approval will default to Budget Holder for all Purchase
						Orders.
					</p>
				</Col>
			</Row>
			<Row
				style={{
					...style,
					fontWeight: 600,
				}}
				align="middle"
			>
				<Col span={7}>Name</Col>
				<Col span={6}>Role</Col>
				<Col span={3}>Project Exp. Approval Limit</Col>
				<Col span={7}>Limit to Project or Task</Col>
				<Col span={1}></Col>
			</Row>
			{data.map((d) => (
				<KeyMemberRow
					id={d.id}
					key={d.id}
					bps={bps}
					isLocked={isLocked}
				/>
			))}
			{formStatus === "validation error" && (
				<Row>
					<span className="qna-question-form-item-validation">
						The Project Accoutant, Project Mananger (Admin) and
						Budget Holder roles are mandatory
					</span>
				</Row>
			)}
			{!isLocked && (
				<Row justify="end" style={{ marginTop: 10 }}>
					<Button
						type="primary"
						onClick={() =>
							dispatch(keyMemberUpserted({ id: newId() }))
						}
					>
						Add Member
					</Button>
				</Row>
			)}
		</Section>
	);
};

const numericParser = (value) => value.replace(/\$\s?|(,*)/g, "");

const currencyFormatter = (value) =>
	getFormattedNumber(value);

const { Option } = Select;

const KeyMemberRow = ({ id, isLocked, bps }) => {
	const dispatch = useDispatch();
	const { appId } = useSelector(selectGlobal);
	const data = useSelector((state) => selectKeyMemberById(state, id));
	const {
		userId,
		email,
		fullName,
		role,
		limit,
		mandatory,
		limitTo,
		disabled,
		isBp,
		noLimit,
	} = data;

	return (
		<Row
			style={{
				...style,
			}}
			align="middle"
		>
			<Col span={7}>
				{role === "Project Accountant" ? (
					<Input
						defaultValue={fullName}
						disabled={disabled || isLocked}
						style={{ width: "100%" }}
					/>
				) : isBp ? (
					<Select
						style={{ width: "100%" }}
						placeholder="Select a Business Partner"
						labelInValue
						value={{ value: userId, label: fullName }}
						disabled={isLocked}
						onChange={(value) =>
							dispatch(
								keyMemberUpserted({
									...data,
									userId: value.value,
									fullName: value.label,
								})
							)
						}
					>
						{bps.map((bp) => (
							<Select.Option key={bp.userId} value={bp.userId}>
								{bp.fullName}
							</Select.Option>
						))}
					</Select>
				) : process.env.REACT_APP_CAPEX_AUTOREGISTER === "true" ? (
					<PersonSearchAd
						value={email}
						onChange={(values) => {
							dispatch(
								keyMemberUpserted({
									...data,
									userId: values.id,
									fullName: values.text,
									email: values.email,
								})
							);
						}}
						defaultOptions={
							userId ? [{ value: email, text: fullName }] : []
						}
						options={{
							disabled: disabled || isLocked,
							includeLabel: true,
						}}
						style={{ width: "100%" }}
					/>
				) : (
					<PersonSearch
						appHierarchyId={appId}
						value={email}
						onChange={(values) => {
							dispatch(
								keyMemberUpserted({
									...data,
									userId: values.id,
									fullName: values.text,
									email: values.email,
								})
							);
						}}
						defaultOptions={
							userId ? [{ value: email, text: fullName }] : []
						}
						options={{
							disabled: disabled || isLocked,
							includeLabel: true,
						}}
						style={{ width: "100%" }}
					/>
				)}
			</Col>
			<Col span={6}>
				<Select
					style={{ width: "100%" }}
					defaultValue={role}
					disabled={mandatory || isLocked}
					onChange={(value) =>
						dispatch(keyMemberUpserted({ ...data, role: value }))
					}
				>
					<Option value="Project Manager (Approver)">
						Project Manager (Approver)
					</Option>
					<Option value="Project Accountant">
						Project Accountant
					</Option>
					<Option value="Project Manager (Admin)">
						Project Manager (Admin)
					</Option>
					<Option value="Senior Project Manager">
						Senior Project Manager
					</Option>
					<Option value="Budget Holder">Budget Holder</Option>
				</Select>
			</Col>
			<Col span={3}>
				<InputNumber
					defaultValue={limit}
					onBlur={(e) =>
						dispatch(
							keyMemberUpserted({
								...data,
								limit: e.target.value,
							})
						)
					}
					formatter={currencyFormatter}
					parser={numericParser}
					controls={false} //turns off useless +/- buttons
					className="text-align-right"
					style={{ width: "100%" }}
					addonBefore={"$"}
					disabled={noLimit || isLocked}
				/>
			</Col>
			<Col span={7}>
				<Input
					defaultValue={limitTo}
					onBlur={(e) =>
						dispatch(
							keyMemberUpserted({
								...data,
								limitTo: e.target.value,
							})
						)
					}
					style={{ width: "100%" }}
					disabled={noLimit || isLocked}
				/>
			</Col>
			<Col span={1}>
				<Button
					type="link"
					danger
					hidden={mandatory}
					onClick={() => {
						dispatch(keyMemberRemoved(id));
					}}
				>
					x
				</Button>
			</Col>
		</Row>
	);
};

export default KeyMembers;
